import { useMemo } from 'react';
import { getFinderVehicleFilter, getVariantFilter } from '../../../shared/useVehicleSuiteIdsFromParameters';
import hasVehicleParameterFilter from '../../helpers/hasVehicleParameterFilter';
import { type FinanceProductFormValues } from '../../index';
import {
    buildFinderVehicleReferenceParameterName,
    finderVehicleParameterKey,
    variantParameterKey,
} from '../LeasePurchaseTable/useVehicleReferenceParameterRecords';
import { type InsuranceFeeTableRecord } from './shared';

type UseInsuranceFeeVehicleReferenceParameterRecords = {
    variant: InsuranceFeeTableRecord;
    finderVehicle: InsuranceFeeTableRecord;
};

const useInsuranceFeeVehicleReferenceParameterRecords = (
    vehicleReferenceParameters: FinanceProductFormValues['vehicleReferenceParameters'],
    mileageUnitLabel: string,
    insuranceFee?: FinanceProductFormValues['insuranceFee']
) =>
    useMemo((): UseInsuranceFeeVehicleReferenceParameterRecords => {
        const vehicleReferenceParameterData = {
            finderVehicle: null,
            variant: null,
        };

        const finderVehicleFilter = getFinderVehicleFilter(vehicleReferenceParameters);
        const variantFilter = getVariantFilter(vehicleReferenceParameters);

        if (!hasVehicleParameterFilter(finderVehicleFilter) && !hasVehicleParameterFilter(variantFilter)) {
            return vehicleReferenceParameterData;
        }

        const variantReferenceParameterId = variantFilter?.engineType.length
            ? variantFilter.engineType.join(', ')
            : null;

        const finderVehicleReferenceParameterId = buildFinderVehicleReferenceParameterName(
            vehicleReferenceParameters.finderVehicle,
            mileageUnitLabel
        );

        if (hasVehicleParameterFilter(variantFilter)) {
            const row = insuranceFee?.table?.find(row => row.variantSuiteId === variantParameterKey);

            vehicleReferenceParameterData.variant = {
                variantSuiteId: variantParameterKey,
                variantName: variantReferenceParameterId,
                value: row?.value ?? 0,
            };
        }

        if (hasVehicleParameterFilter(finderVehicleFilter)) {
            const row = insuranceFee?.table?.find(row => row.variantSuiteId === finderVehicleParameterKey);

            vehicleReferenceParameterData.finderVehicle = {
                variantSuiteId: finderVehicleParameterKey,
                variantName: finderVehicleReferenceParameterId,
                value: row?.value ?? 0,
            };
        }

        return vehicleReferenceParameterData;
    }, [insuranceFee?.table, mileageUnitLabel, vehicleReferenceParameters]);

export default useInsuranceFeeVehicleReferenceParameterRecords;
